<template>
  <div>
    <div class="timeline-group" v-for="[timeline, goals] in groupedGoals" :key="timeline">
      <div class="d-flex align-items-center text-normal bolder timeline">
        <div class="pie-icon-wrapper flex-center">
          <timeline-pie
            :id="'timeline-pie-' + timeline"
            :size="14"
            :completedPercent="getTimelineCompletedPercent(timeline)"
            v-if="isActive(timeline)"
          />
          <b-popover
            :target="'timeline-pie-' + timeline"
            triggers="hover"
            placement="bottom"
            delay="500"
            offset="0"
            v-if="isActive(timeline)"
          >
            {{ getDaysLeftInTimeline(intervalWithId(timeline)) }} days left in timeline
          </b-popover>
        </div>
        {{ getTimelineName(timeline) }}
      </div>
      <rearrangeable-goals-list
        :goals="goals"
        :org-id="orgId"
        :selected-timeline="selectedTimeline"
        :source="source"
        @arrange="$emit('arrange', $event)"
        @move-goal="$emit('move-goal')"
      />
    </div>
  </div>
</template>

<script>
import RearrangeableGoalsList from '@/components/goal/RearrangeableGoalsList'
import TimelinePie from '@/components/TimelinePie'
import { BPopover } from 'bootstrap-vue'
import * as R from 'ramda'
import {
  DurationType,
  getDaysLeftInTimeline,
  getTimelineCompletedPercent,
  IntervalId,
  intervalWithId,
  isActiveInterval
} from '@/interval'

/**
 * this component shows goals grouped by their timeline. Timeline is sorted in a way that recent timeline comes first.
 *
 * @emits
 * - delete-goal
 * - archive-goal
 */
export default {
  name: 'TimelineWiseGoals',
  props: ['goals', 'selectedTimeline', 'orgId', 'source'],
  components: {
    TimelinePie,
    BPopover,
    RearrangeableGoalsList
  },
  computed: {
    timelineOrder() {
      return this.selectedTimeline.id === IntervalId.PAST ? 'desc' : 'asc'
    },
    groupedGoals() {
      const groupedGoals = R.groupBy(goal => goal.timeline, this.goals)
      const timelineGoalsPairs = R.toPairs(groupedGoals)
      let timelineWiseSortedGoals = R.sort(([timelineA], [timelineB]) => {
        const firstTimeline = intervalWithId(timelineA)
        const secondTimeline = intervalWithId(timelineB)
        let comparatorA = new Date(firstTimeline.start).getTime()
        let comparatorB = new Date(secondTimeline.start).getTime()
        if (firstTimeline.start === secondTimeline.start) {
          comparatorB = new Date(firstTimeline.end).getTime()
          comparatorA = new Date(secondTimeline.end).getTime()
        }
        let sign = 1
        if (this.timelineOrder === 'asc') {
          sign = -1
        }
        return (comparatorB - comparatorA) * sign
      }, timelineGoalsPairs)
      if (this.selectedTimeline.duration === DurationType.QUARTER) {
        // move selected quarter goals to the top
        const index = timelineGoalsPairs.findIndex(
          ([timelineId]) => this.selectedTimeline.id === timelineId
        )
        timelineWiseSortedGoals = R.move(index, 0, timelineWiseSortedGoals)
      }
      return timelineWiseSortedGoals
    }
  },
  methods: {
    getTimelineName(timeline) {
      return intervalWithId(timeline, { matchExact: true }).name
    },
    getTimelineCompletedPercent(timeline) {
      return getTimelineCompletedPercent(intervalWithId(timeline))
    },
    isActive(timeline) {
      return isActiveInterval(intervalWithId(timeline))
    },
    intervalWithId,
    getDaysLeftInTimeline
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.timeline {
  padding-left: 56px;
  margin: 25px 0 16px 0;
}

.pie-icon-wrapper {
  @include dimension(22px, 22px);
  margin-right: 5px;
}

@include media-breakpoint-down(md) {
  .timeline {
    padding-left: 12px;
  }
}
</style>
